/**
 * Адреса окружения. Читаются один раз при загрузке модуля: отсутствие
 * обязательной переменной обязано падать здесь же, внятной ошибкой, а не
 * отдавать `undefined` в fetch или в адрес WebSocket где-то в середине партии.
 */

const isDev = import.meta.env.DEV;

/**
 * VITE_DEV_OFFLINE=1 работает только под `vite dev`. Бутстрап (src/app/bootstrap.ts)
 * поднимается на FakeTransport и фальшивой сессии вместо POST /auth/telegram —
 * сервер в этом режиме не нужен вовсе, поэтому адреса ниже разрешено не задавать.
 * В прод-сборке переменная не имеет силы: `isDev` там литерально `false`, и код,
 * который на неё смотрит (src/app/bootstrap.ts), выпадает из бандла на dead-code
 * elimination — см. отчёт дев-платформы.
 */
export const devOffline = isDev && import.meta.env.VITE_DEV_OFFLINE === '1';

/**
 * VITE_DEV_LIVE=1 — живой бэкенд через сам dev-сервер: `/api` и `/ws` он
 * проксирует (vite.config.ts), а в обычном браузере ещё и подписывает initData
 * (scripts/dev-initdata.mjs). Адреса сервера в этом режиме не задаются: страница
 * и API живут на одном источнике, поэтому и CORS, и проверка Origin у `/ws`
 * перестают существовать как вопрос.
 */
export const devLive = isDev && import.meta.env.VITE_DEV_LIVE === '1';

if (devOffline && devLive) {
  throw new Error(
    'VITE_DEV_OFFLINE и VITE_DEV_LIVE включены разом. Это взаимоисключающие режимы: ' +
      'первый поднимает FakeTransport, второй ходит в настоящий сервер.',
  );
}

function required(name: 'VITE_API_ORIGIN' | 'VITE_WS_URL'): string {
  const raw = import.meta.env[name];
  if (typeof raw === 'string' && raw.length > 0) return raw;
  if (devOffline) return ''; // сюда никто не постучится — транспорт фальшивый
  if (devLive) return ''; // адрес свой же, он выводится ниже из location
  throw new Error(
    `Переменная окружения ${name} не задана. Скопируйте .env.example в .env и укажите адрес сервера.`,
  );
}

/**
 * Адрес партии. В живом режиме он выводится из адреса страницы: dev-сервер
 * проксирует `/ws` на бэкенд, и зашивать сюда хост туннеля незачем — он меняется
 * при каждом перезапуске туннеля, а забытое старое значение выглядит как
 * необъяснимый обрыв связи.
 */
function socketUrl(raw: string): string {
  if (raw.length > 0) return raw;
  if (!devLive) return raw;
  const origin = globalThis.location?.origin ?? '';
  return `${origin.replace(/^http/, 'ws')}/ws`;
}

/**
 * Хост без пути. Версия API дописывается клиентом (src/api/rest.ts), поэтому
 * `/api/v1` в переменной — не безобидная избыточность, а удвоенный префикс в
 * каждом запросе. Ошибка тихая: 404 вместо ответа, — ловим её здесь.
 *
 * Переменная поэтому и переименована из `VITE_API_URL`: старое значение с
 * префиксом молча сломало бы все запросы, а незаданная переменная падает
 * сразу и с объяснением.
 */
function origin(raw: string): string {
  const trimmed = raw.replace(/\/+$/, '');
  if (/\/api(\/v\d+)?$/.test(trimmed)) {
    throw new Error(
      `VITE_API_ORIGIN содержит путь «${trimmed.replace(/^.*(\/api.*)$/, '$1')}». ` +
        'Укажите только хост: версию API дописывает клиент.',
    );
  }
  return trimmed;
}

export interface AppConfig {
  /** Хост REST без пути: `/api/<версия>` дописывает RestClient. */
  apiOrigin: string;
  /** WebSocket партии. Маршрут /ws смонтирован вне /api, поэтому адрес отдельный. */
  wsUrl: string;
  devOffline: boolean;
}

export const config: AppConfig = {
  apiOrigin: origin(required('VITE_API_ORIGIN')),
  wsUrl: socketUrl(required('VITE_WS_URL')),
  devOffline,
};
